import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, HostListener } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AppInfoService } from 'core/app-info/app-info.service';
import { currencyCodeSymbols } from 'core/app-info/country-specifics.enum';
import { SignalRService } from 'core/signalR-service/signal-r.service';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { DeviceDetectorService } from 'ngx-device-detector';
import { SignalrConnectionType } from '../../../shared/entities/signalrConnectionType.enum';
import { SignalrMethodName } from '../../../shared/entities/signalrMethodName.enum';
import { SignalrNotification } from '../../../shared/entities/SignalrNotification.class';
import { B2BUser } from '../entities/b2b-user-info';
import { AddCartItemRequest, CartItem, CartItemsRequest, CartItemsResponse,
  DeleteCartItemRequest, DeleteCartItemResponse,UpdateCartItemQuantityRequest} from '../entities/cart-item';
import { B2bUserCartService } from '../services/b2b-user-cart.service';
import { B2bCartNotificationEvent } from '../b2b-cart-notifications/b2b-cart-notifications.component';
import { B2bCartItemDialogComponent, CartItemDialogData } from '../b2b-cart-item-dialog/b2b-cart-item-dialog.component';
import { Product } from '../../../pos/quotes/entities/products.class';

@Component({
  selector: 'app-b2b-user-cart',
  templateUrl: './b2b-user-cart.component.html',
  styleUrls: ['./b2b-user-cart.component.scss'],
  standalone: false
})
export class B2bUserCartComponent implements OnInit, OnDestroy {
  @Input() user: B2BUser;
  @Input() userIndex: number;
  @Input() editMode = false;
  @Input() customerId: string;
  @Input() bpNumber: string;
  @Input() countryCodeId: number;
  @Output() cartItemsLoaded = new EventEmitter<{ userIndex: number, cartItems: CartItem[] }>();

  subs = new Subscription();
  displayedColumns: string[] = [];
  appStrings;
  cartItems: CartItem[] = [];
  cartCurrencySymbol: string;
  cartCurrencyCode: string;
  loading = false;
  hasLoaded = false;
  cartFormArray: FormArray;
  isMobile: boolean;

  constructor(
    private cartService: B2bUserCartService,
    public appInfoService: AppInfoService,
    private signalrService: SignalRService,
    private dialog: MatDialog,
    private deviceService: DeviceDetectorService
  ) { }

  ngOnInit(): void {
    this.appStrings = this.appInfoService.appStrings;
    this.isMobile = this.deviceService.isMobile();
    this.setDisplayedColumns();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  @HostListener('document:keydown.enter', ['$event'])
  handleEnterKey(event: KeyboardEvent): void {
    // Only trigger on desktop in edit mode
    if (this.editMode && !this.isMobile) {
      this.addItem();
      event.preventDefault();
    }
  }

  private setDisplayedColumns(): void {
    if (this.editMode) {
      this.displayedColumns = ['itemNumber', 'quantity', 'description', 'unitPrice', 'totalPrice', 'notifications', 'actions'];
    } else {
      this.displayedColumns = ['itemNumber', 'quantity', 'price', 'description', 'notifications'];
    }
  }

  loadCartItems(): void {
    // Only load once
    if (this.hasLoaded || this.loading) {
      return;
    }

    this.loading = true;
    this.subs.add(this.fetchCartItems().subscribe({
      next: () => {
        this.loading = false;
        this.hasLoaded = true;
        this.cartItemsLoaded.emit({ userIndex: this.userIndex, cartItems: this.cartItems });
      },
      error: () => {
        this.loading = false;
      }
    }));
  }

  private fetchCartItems(): Observable<CartItemsResponse> {
    const payload: CartItemsRequest = {
      UserId: this.user.UserId,
      ShipToBp: this.bpNumber
    };

    return this.cartService.getCartItems(payload).pipe(
      tap((response: CartItemsResponse) => {
        if (response?.Data?.CartItems) {
          this.cartItems = [...response.Data.CartItems];
        }
        if (response?.Data?.Currency) {
          this.cartCurrencyCode = response.Data.Currency;
          this.cartCurrencySymbol = currencyCodeSymbols[response.Data.Currency];
        }
        if (this.editMode) {
          this.initializeFormControls();
        }
      })
    );
  }

  private initializeFormControls(): void {
    // Add empty row at the beginning for new item entry
    this.cartItems.unshift(this.createEmptyItem());

    // Create form groups for all cart items with validators
    const itemFormGroups = this.cartItems.map(item => new FormGroup({
      itemNumber: new FormControl(item.ModelNumber, [Validators.required]),
      quantity: new FormControl(item.Quantity, [Validators.required, Validators.min(1)])
    }));

    this.cartFormArray = new FormArray(itemFormGroups);
  }

  private createEmptyItem(): CartItem {
    return {
      ModelNumber: '',
      Quantity: 1,
    } as CartItem;
  }

  getItemFormGroup(index: number): FormGroup {
    return this.cartFormArray?.at(index) as FormGroup;
  }

  getControl(index: number, fieldName: 'itemNumber' | 'quantity'): FormControl {
    return this.getItemFormGroup(index)?.get(fieldName) as FormControl;
  }

  getTotalPrice(item: CartItem): number {
    return item.Quantity * (item.NetPrice ?? 0);
  }

  private validateFormGroup(index: number): boolean {
    const formGroup = this.getItemFormGroup(index);
    if (!formGroup) {
      return false;
    }
    formGroup.markAllAsTouched();
    // Check if form is valid
    if (formGroup.invalid) {
      return false;
    }
    return true;
  }

  updateCartItemQuantity(item: CartItem, index: number): void {
    if (!this.validateFormGroup(index)) {
      return;
    }

    const quantity = Number(this.getItemFormGroup(index).get('quantity')?.value);
    if (quantity === item.Quantity) {
      return;
    }

    this.performUpdateQuantity(item.ShoppingCartItemId, quantity, () => {
      this.getControl(index, 'quantity').setValue(item.Quantity);
    });
  }

  updateCartItemNumber(item: CartItem, index: number): void {
    if (!this.validateFormGroup(index)) {
      return;
    }

    const newItemNumber = this.getItemFormGroup(index).get('itemNumber')?.value;
    // If item number hasn't changed, do nothing
    if (newItemNumber.toUpperCase().trim() === item.ModelNumber) {
      return;
    }

    this.performReplaceItem(item.ShoppingCartItemId, newItemNumber, item.Quantity, () => {
      this.getControl(index, 'itemNumber').setValue(item.ModelNumber);
    });
  }

  deleteItem(item: CartItem): void {
    this.loading = true;
    this.subs.add(this.cartService.deleteCartItem(this.createDeleteItemPayload(item.ShoppingCartItemId))
      .subscribe({
        next: () => {
          this.loading = false;
          this.reloadItems();
        },
        error: () => {
          this.loading = false;
        }
      }));
  }

  addItem(): void {
    if (this.isMobile) {
      this.openItemDialog(true, 0);
      return;
    }

    if (!this.validateFormGroup(0)) {
      return;
    }

    const newItemFormGroup = this.getItemFormGroup(0);
    const itemNumber = newItemFormGroup.get('itemNumber')?.value;
    const quantity = newItemFormGroup.get('quantity')?.value;

    this.performAddItem(itemNumber, quantity, () => {
      newItemFormGroup.reset({ itemNumber: '', quantity: 1 });
    });
  }

  onProductSelected(product: Product): void {
    if (!product || !product.productNumber) {
      return;
    }

    if (this.isMobile) {
      this.performAddItem(product.productNumber, product.quantity || 1);
    } else {
      const newItemFormGroup = this.getItemFormGroup(0);
      newItemFormGroup.patchValue({
        itemNumber: product.productNumber,
        quantity: product.quantity || 1
      });

      this.addItem();
    }
  }

  openItemDialog(isNewItem: boolean, index: number): void {
    const item = isNewItem ? this.createEmptyItem() : this.cartItems[index];

    const dialogData: CartItemDialogData = {
      item,
      isNewItem,
      currencySymbol: this.cartCurrencySymbol
    };

    const dialogRef = this.dialog.open(B2bCartItemDialogComponent, {
      width: '95%',
      maxWidth: '600px',
      data: dialogData,
      disableClose: false,
      panelClass: 'cart-item-dialog'
    });

    this.subs.add(
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.handleDialogResult(result, item);
        }
      })
    );
  }

  private handleDialogResult(result: any, originalItem: CartItem): void {
    const { itemNumber, quantity, isNewItem } = result;

    if (isNewItem) {
      this.performAddItem(itemNumber, quantity);
      return;
    }

    const itemNumberChanged = itemNumber !== originalItem.ModelNumber;
    const quantityChanged = quantity !== originalItem.Quantity;

    if (!itemNumberChanged && !quantityChanged) {
      return;
    }

    if (itemNumberChanged) {
      this.performReplaceItem(originalItem.ShoppingCartItemId, itemNumber, quantity);
    } else {
      this.performUpdateQuantity(originalItem.ShoppingCartItemId, quantity);
    }
  }

  private performAddItem(itemNumber: string, quantity: number, onError?: () => void): void {
    this.loading = true;
    this.subs.add(this.cartService.addCartItem(this.createAddItemPayload(itemNumber, quantity))
      .subscribe({
        next: () => {
          this.loading = false;
          this.reloadItems();
        },
        error: () => {
          this.loading = false;
          onError?.();
        }
      }));
  }

  private performUpdateQuantity(shoppingCartItemId: number, quantity: number, onError?: () => void): void {
    const payload: UpdateCartItemQuantityRequest = {
      ShoppingCartItemId: shoppingCartItemId,
      Quantity: quantity,
      UserId: this.user.UserId
    };

    this.loading = true;
    this.subs.add(this.cartService.updateCartItemQuantity(payload).subscribe({
      next: () => {
        this.loading = false;
        this.reloadItems();
      },
      error: () => {
        this.loading = false;
        onError?.();
      }
    }));
  }

  private performReplaceItem(shoppingCartItemId: number, newItemNumber: string, quantity: number, onError?: () => void): void {
    this.loading = true;
    this.subs.add(this.cartService.deleteCartItem(this.createDeleteItemPayload(shoppingCartItemId)).subscribe({
      next: (deleteResponse: DeleteCartItemResponse) => {
        if (deleteResponse?.Code === 'Success') {
          this.subs.add(this.cartService.addCartItem(this.createAddItemPayload(newItemNumber, quantity)).subscribe({
            next: () => {
              this.loading = false;
              this.reloadItems();
            },
            error: () => {
              this.loading = false;
              onError?.();
            }
          }));
        }
      },
      error: () => {
        this.loading = false;
        onError?.();
      }
    }));
  }

  reloadItems(): void {
    this.hasLoaded = false;
    this.loadCartItems();
  }

  private createAddItemPayload(modelNumber: string, quantity: number): AddCartItemRequest {
    return {
      Product: {
        ModelNumber: modelNumber.toUpperCase().trim(),
        Quantity: Number(quantity)
      },
      UserId: this.user.UserId,
      ShipToBp: this.bpNumber
    };
  }

  private createDeleteItemPayload(shoppingCartItemId: number): DeleteCartItemRequest {
    return {
      ShoppingCartItemId: shoppingCartItemId,
      UserId: this.user.UserId
    };
  }

  handleNotificationAction(event: B2bCartNotificationEvent): void {
    if (!event?.item) {
      return;
    }

    switch (event.action) {
      case 'Remove':
        this.deleteItem(event.item);
        break;
      case 'Replace':
        if (event.item.SuperseededBy) {
          console.log('Replace item');
        }
        break;
      case 'Keep':
        // TODO - implement keep functionality if needed, currently just logs the action
        console.log('Keep');
        break;
    }
  }

  generateQuote(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.loading = true;

      const executeQuote = () => {
        this.signalrService.startConnection(SignalrConnectionType.GenerateQuote)
          .then(() => {
            const payload = {
              customerId: this.customerId,
              leadId: null,
              contactName: `${this.user.FirstName} ${this.user.LastName}`.trim(),
              contactPhone: this.user.Phone ?? null,
              contactEmail: this.user.Email ?? null,
              cartData: this.buildCartData(),
              notification: new SignalrNotification(
                this.signalrService.connectionId,
                null,
                SignalrMethodName.generateQuote
              )
            };

            this.subs.add(
              this.cartService.generateQuote(payload).subscribe({
                next: () => resolve(),
                error: (error) => {
                  this.loading = false;
                  reject(error);
                }
              })
            );
          })
          .catch((error) => {
            this.loading = false;
            reject(error);
          });
      };

      if (!this.hasLoaded) {
        this.subs.add(this.fetchCartItems().subscribe({
          next: () => {
            this.hasLoaded = true;
            this.loading = false;
            executeQuote();
          },
          error: (error) => {
            this.loading = false;
            reject(error);
          }
        }));
      } else {
        executeQuote();
      }
    });
  }

  private buildCartData(): any {
    const itemsNo = this.cartItems.reduce((sum, item) => sum + item.Quantity, 0);
    const total = this.cartItems.reduce((sum, item) => sum + (item.Quantity * (item.NetPrice ?? 0)), 0);
    const weight = this.cartItems.reduce((sum, item) => sum + ((item.Weight ?? 0) * item.Quantity), 0);
    const lineItems = this.cartItems.length;

    return {
      ItemsNo: itemsNo,
      Total: total,
      Currency: this.cartCurrencyCode,
      Weight: weight,
      LineItems: lineItems,
      ShippingAndHandling: 0,
      CartItems: this.cartItems.map(item => ({
        ShoppingCartItemId: item.ShoppingCartItemId?.toString() ?? null,
        Active: item.Active ?? null,
        IsSuperseeded: item.IsSuperseeded ?? false,
        SuperseededBy: item.SuperseededBy?.toString() ?? null,
        ProductFound: item.ProductFound ?? false,
        ProductId: (item.ProductId ?? 0).toString(),
        ModelNumber: item.ModelNumber,
        Quantity: item.Quantity.toString(),
        SignalCode: item.SignalCode ?? null,
        LMCode: item.LmCode?.toString() ?? null,
        IsLimitedMarketing: item.IsLimitedMarketing ?? null,
        NetPrice: (item.NetPrice ?? 0).toString(),
        ListPrice: (item.ListPrice ?? 0).toString(),
        Name: item.Name ?? null,
        LifeCycleId: item.LifeCycleId?.toString() ?? null,
        Weight: (item.Weight ?? 0).toString(),
        Description: item.Description ?? null,
        SuperItemLMCO: item.SuperItemLMCO?.toString() ?? null,
        SuperItemCycle: (item.SuperItemCycle ?? 0).toString()
      }))
    };
  }
}
